import { useMemo } from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import type { ProcessingMode } from "../lib/cleanseSchema";

type PreviewRow = Record<string, unknown>;

export interface PreviewTableProps {
  rows: PreviewRow[];
  csvHeaders: string[];
  mode: ProcessingMode;
  line1Col: string;
  postcodeCol: string;
  uprnCol: string;
}

const PREVIEW_ROW_LIMIT = 5;

const CheckIcon = ({ className }: { className: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
      clipRule="evenodd"
    />
  </svg>
);

export function PreviewTable({
  rows,
  csvHeaders,
  mode,
  line1Col,
  postcodeCol,
  uprnCol,
}: PreviewTableProps) {
  const autoDetectedSet = useMemo(() => {
    const set = new Set<string>();
    if (mode === "cleanse") {
      if (line1Col) set.add(line1Col);
      if (postcodeCol) set.add(postcodeCol);
    } else if (uprnCol) {
      set.add(uprnCol);
    }
    return set;
  }, [mode, line1Col, postcodeCol, uprnCol]);

  const columns = useMemo<ColumnDef<PreviewRow>[]>(
    () =>
      csvHeaders.map((header) => ({
        id: header,
        accessorFn: (row) => row[header],
        header: () => (
          <div className="flex items-center gap-2">
            {header}
            {autoDetectedSet.has(header) && (
              <CheckIcon className="w-3 h-3 text-green-400" />
            )}
          </div>
        ),
        cell: ({ getValue }) => {
          const v = getValue() as string | undefined;
          return v || "-";
        },
      })),
    [csvHeaders, autoDetectedSet]
  );

  const data = useMemo(() => rows.slice(0, PREVIEW_ROW_LIMIT), [rows]);

  // TanStack Table returns non-memoizable instance methods by design; React
  // Compiler skips this hook automatically.
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border border-slate-700/50 rounded-lg">
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr
              key={hg.id}
              className="bg-slate-800/50 border-b border-slate-600"
            >
              {hg.headers.map((header) => {
                const auto = autoDetectedSet.has(header.column.id);
                return (
                  <th
                    key={header.id}
                    className={`text-left py-3 px-4 font-medium ${
                      auto
                        ? "text-green-300 bg-green-950/30 border-green-700/50"
                        : "text-slate-300"
                    }`}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              className="border-b border-slate-700/30 hover:bg-slate-800/30 transition-colors"
            >
              {row.getVisibleCells().map((cell) => {
                const auto = autoDetectedSet.has(cell.column.id);
                const raw = cell.getValue();
                const title = typeof raw === "string" ? raw : undefined;
                return (
                  <td
                    key={cell.id}
                    className={`py-3 px-4 ${
                      auto ? "text-green-200 bg-green-950/20" : "text-slate-400"
                    }`}
                    title={title}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
